import type {
  Company,
  CompanyAddress,
  CompanyAddressInput,
  CompanyChange,
  CompanyInput,
  CompanySummary,
} from "@optymyse/suite-server/link";

/**
 * Link's companies as this app reads and writes them: SuiteServer's records (`@optymyse/suite-server/link`), which
 * since its 0.3.0 hold a company's postal `address` and its `notes` (Link's directory schema version 6, the owner's
 * decision of 23 October 2026). A company's record holds both; a page of companies holds neither, as Link's pages
 * don't (Link docs/PRODUCT_API.md, "Companies"), so nothing of an address is in the customers list. Every type of a
 * company the app uses is named here, beside the limits Link holds each field to, which SuiteServer doesn't carry.
 */
export type { Company, CompanyAddress, CompanyAddressInput, CompanyChange, CompanyInput, CompanySummary };

export const ADDRESS_PARTS = [
  "line1",
  "line2",
  "city",
  "region",
  "postcode",
  "country",
] as const satisfies readonly (keyof CompanyAddress)[];

/**
 * The most each field holds, as Link's limits say (Link docs/PRODUCT_API.md, "Companies" and "Limits"). Link checks
 * every one again, and its refusal is the authority: these only refuse early what it would.
 */
export const COMPANY_LIMITS = Object.freeze({
  display_name: 240,
  legal_name: 300,
  domain: 253,
  website: 2_048,
  notes: 10_000,
  /** An address's two lines, its city and its region, each. */
  address_line: 200,
  postcode: 40,
});

/** An ISO 3166-1 alpha-2 code, as Link keeps a country: two capital letters, such as GB (not UK, nor gb). */
export const COUNTRY_CODE = /^[A-Z]{2}$/;
